import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { billingAuthHeaders } from '../utils/billingApi';
import { CARD_ONLY_PAYMENT_ELEMENT_OPTIONS } from '../utils/stripePaymentElementOptions';
import {
    confirmPaymentElementSetup,
    consumeOwnedStripeSetupRedirect,
    rememberExpectedSetupIntent,
    withoutStripeSetupRedirectParams,
} from '../utils/stripeSetupConfirm';
import { USER_TYPES, normalizeUserType } from '../constants/userTypes';
import { isLocalDevHostname } from '../utils/apiUrls';
import PhoneNumberInput from './PhoneNumberInput';
import { phoneDigits } from '../utils/phoneNumber';
import SmsConsentNotice from './SmsConsentNotice';
import SupportHelpCard from './SupportHelpCard';
import {
    PaymentLegalAcceptanceCheckbox,
    paymentSetupSubmitEnabled,
} from './PaymentLegalAcceptance';
import './ContractWizard.css';

const billingApiRoot = (backendUrl) => backendUrl.replace(/\/contract\/?$/, '');

const PLACEHOLDER_LICENSE_VALUES = new Set(['pending', 'n/a', 'na', 'none', 'tbd', 'unknown']);

function isPlaceholderLicense(value) {
    const normalized = (value || '').trim().toLowerCase();
    return !normalized || PLACEHOLDER_LICENSE_VALUES.has(normalized);
}

function isKnownUserType(value) {
    const normalized = normalizeUserType(value);
    return USER_TYPES.some(({ value: v }) => v === normalized);
}

function effectiveUserType(formData) {
    const normalized = normalizeUserType(formData.user_type);
    const candidate = normalized || formData.user_type || '';
    return isKnownUserType(candidate) ? candidate : '';
}

function sanitizeProfileData(data) {
    if (!data) {
        return data;
    }
    const next = { ...data };
    const license = (next.license_number || '').trim().toLowerCase();
    if (license && PLACEHOLDER_LICENSE_VALUES.has(license)) {
        next.license_number = '';
        if ((next.licensed_state || '').trim().toUpperCase() === 'OK') {
            next.licensed_state = '';
        }
    }
    return next;
}

function getProfileValidationErrors(formData) {
    const errors = {};
    if (!formData.name?.trim()) errors.name = 'Name is required';
    if (phoneDigits(formData.phone_number).length !== 10) {
        errors.phone_number = 'Enter a 10-digit phone number';
    }

    const userType = effectiveUserType(formData);
    if (!userType) {
        errors.user_type = 'User type is required';
    }
    if (['real_estate_agent', 'managing_broker', 'mortgage_broker', 'lender'].includes(userType)) {
        if (isPlaceholderLicense(formData.license_number)) {
            errors.license_number = 'License number is required';
        }
        if (!formData.licensed_state?.trim()) errors.licensed_state = 'State is required';
    }
    if (!formData.company_name?.trim()) errors.company_name = 'Company name is required';
    if (
        userType !== 'buyer'
        && userType !== 'seller'
        && userType !== 'real_estate_agent'
        && !formData.company_address?.trim()
    ) {
        errors.company_address = 'Company address is required';
    }
    return errors;
}

export function PaymentStepForm({
    clientSecret,
    acknowledged,
    onAcknowledgedChange,
    legalAccepted,
    onLegalAcceptedChange,
    onSuccess,
    onError,
}) {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const canSubmit = paymentSetupSubmitEnabled({
        stripeReady: Boolean(stripe && elements),
        submitting,
        trialAcknowledged: acknowledged,
        legalAccepted,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const { error, setupIntent, intentId, alreadySucceeded } = await confirmPaymentElementSetup(
                stripe,
                elements,
                { clientSecret },
            );
            if (error && !alreadySucceeded) {
                onError(error.message);
                return;
            }
            if (intentId && (setupIntent?.status === 'succeeded' || alreadySucceeded)) {
                await onSuccess(intentId);
            } else {
                onError('Could not verify card setup. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement options={CARD_ONLY_PAYMENT_ELEMENT_OPTIONS} />

            <label
                style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'flex-start',
                    marginTop: '16px',
                    marginBottom: '20px',
                    lineHeight: 1.5,
                    cursor: 'pointer',
                }}
            >
                <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => onAcknowledgedChange(e.target.checked)}
                    style={{ marginTop: '4px' }}
                />
                <span>
                    I understand that after my free trial, my monthly subscription will begin
                    and transaction fees may apply at deal close. I can switch plans or cancel
                    at any time from my Profile.
                </span>
            </label>

            <PaymentLegalAcceptanceCheckbox
                checked={legalAccepted}
                onChange={onLegalAcceptedChange}
            />

            <div
                className="wizard-actions"
                style={{ marginTop: '20px', justifyContent: 'center', borderTop: 'none', paddingTop: 0 }}
            >
                <button
                    type="submit"
                    className="wizard-button primary"
                    disabled={!canSubmit}
                >
                    {submitting ? 'Starting Free Trial…' : 'Start Free Trial'}
                </button>
            </div>
        </form>
    );
}

export default function FreeTrialSignupWizard({
    user,
    backendUrl,
    profileData,
    userTypeLocked = false,
    onProfileSaved,
    onComplete,
}) {
    const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
    const [searchParams, setSearchParams] = useSearchParams();
    const billingBase = `${billingApiRoot(backendUrl)}/billing`;
    const redirectHandledRef = useRef(false);
    const [formData, setFormData] = useState(() => sanitizeProfileData(profileData));
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [publishableKey, setPublishableKey] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [setupIntentAttempt, setSetupIntentAttempt] = useState(0);
    const [acknowledged, setAcknowledged] = useState(false);
    const [legalAccepted, setLegalAccepted] = useState(false);
    const [finishingRedirect, setFinishingRedirect] = useState(false);

    const profileErrors = useMemo(() => getProfileValidationErrors(formData), [formData]);
    const profileComplete = Object.keys(profileErrors).length === 0;
    const profileOnly = formData?.profile_only_waitlist === true;
    const [step, setStep] = useState(() => (
        typeof window !== 'undefined'
        && isLocalDevHostname(window.location.hostname)
        && typeof URLSearchParams !== 'undefined'
        && new URLSearchParams(window.location.search).get('preview_signup_wizard') === '1'
            ? 1
            : 0
    ));
    const totalSteps = profileOnly ? 1 : 2;

    useEffect(() => {
        setFormData(sanitizeProfileData(profileData));
    }, [profileData]);

    const loadBilling = useCallback(async () => {
        const cfg = await axios.get(`${billingBase}/config`);
        setPublishableKey(cfg.data.publishable_key);
    }, [billingBase]);

    const beginSetupIntent = useCallback(async () => {
        const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
        const si = await axios.post(
            `${billingBase}/setup-intent`,
            { email: user.email },
            { headers },
        );
        rememberExpectedSetupIntent(si.data.client_secret || si.data.setup_intent_id);
        setClientSecret(si.data.client_secret);
    }, [billingBase, user?.email, getAccessTokenSilently, getIdTokenClaims]);

    useEffect(() => {
        if (!user?.email) return;
        loadBilling().catch((err) => setError(err.response?.data?.error || err.message));
    }, [user?.email, loadBilling]);

    useEffect(() => {
        if (step !== 1 || clientSecret || redirectHandledRef.current) return;
        beginSetupIntent().catch((err) => {
            setError(err.response?.data?.error || err.message);
        });
    }, [step, clientSecret, beginSetupIntent, setupIntentAttempt]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleSaveProfile = async () => {
        const validation = getProfileValidationErrors(formData);
        if (Object.keys(validation).length > 0) {
            setErrors(validation);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
            await axios.post(`${backendUrl}/user/profile`, formData, { headers });
            onProfileSaved?.(formData);
            if (profileOnly) {
                onComplete?.();
                return;
            }
            setStep(1);
        } catch (err) {
            setError(err.response?.data?.error || 'Could not save profile.');
        } finally {
            setSaving(false);
        }
    };

    const handlePaymentAckChange = (ack) => {
        setAcknowledged(ack);
    };

    const handleLegalAcceptedChange = (accepted) => {
        setLegalAccepted(accepted);
    };

    const completeBillingSetup = useCallback(async (setupIntentId) => {
        const headers = await billingAuthHeaders({ getAccessTokenSilently, getIdTokenClaims });
        await axios.post(`${backendUrl}/user/profile`, formData, { headers });
        const confirmResp = await axios.post(`${billingBase}/payment-method/confirm`, {
            email: user.email,
            setup_intent_id: setupIntentId,
        }, { headers });
        sessionStorage.removeItem(`keyflo_access_status_${user.email}`);
        sessionStorage.removeItem(`keyflo_access_data_${user.email}`);
        const billing = confirmResp.data?.billing || {};
        window.dispatchEvent(new CustomEvent('keyflo-access-changed', {
            detail: {
                status: 'APPROVED',
                ...billing,
                billing_ready: billing.billing_ready === true,
            },
        }));
        onComplete?.();
    }, [
        backendUrl,
        billingBase,
        formData,
        getAccessTokenSilently,
        getIdTokenClaims,
        onComplete,
        user.email,
    ]);

    useEffect(() => {
        if (redirectHandledRef.current || !user?.email || profileOnly) return;
        if (searchParams.get('setup_intent') || searchParams.get('redirect_status')) {
            setSearchParams(withoutStripeSetupRedirectParams(searchParams), { replace: true });
        }
        const redirect = consumeOwnedStripeSetupRedirect(searchParams);
        if (!redirect) return;
        redirectHandledRef.current = true;
        if (redirect.succeeded && redirect.setupIntentId) {
            setFinishingRedirect(true);
            completeBillingSetup(redirect.setupIntentId)
                .catch((err) => {
                    redirectHandledRef.current = false;
                    setFinishingRedirect(false);
                    setError(err.response?.data?.error || err.message || 'Could not finish card setup.');
                    setStep(1);
                });
            return;
        }
        redirectHandledRef.current = false;
        setError('Card authentication did not complete. Please try again.');
        setStep(1);
    }, [completeBillingSetup, profileOnly, searchParams, setSearchParams, user?.email]);

    const retrySetupIntent = () => {
        redirectHandledRef.current = false;
        setError(null);
        setClientSecret(null);
        setSetupIntentAttempt((attempt) => attempt + 1);
        loadBilling().catch((err) => setError(err.response?.data?.error || err.message));
    };

    const stripePromise = publishableKey ? loadStripe(publishableKey) : null;
    const userType = effectiveUserType(formData);

    return (
        <div className="wizard-overlay">
            <div className="wizard-container" style={{ maxWidth: '640px' }}>
                <div className="wizard-header">
                    <h2>{profileOnly ? 'Complete your profile' : 'Start your Free Trial'}</h2>
                </div>

                <div className="wizard-progress">
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
                        />
                    </div>
                    <span className="progress-text">
                        Step {step + 1} of {totalSteps}
                    </span>
                </div>

                <div className="wizard-content">
                    <SupportHelpCard supportLine={(profileData && profileData.support_line) || ''} />
                    {error && (
                        <p style={{ color: '#b91c1c', marginTop: 0 }}>{error}</p>
                    )}

                    {finishingRedirect && (
                        <p style={{ color: '#374151', lineHeight: 1.6 }}>
                            Finishing payment setup…
                        </p>
                    )}

                    {!finishingRedirect && step === 0 && (
                        <>
                            <div className="wizard-question">
                                <h3>Complete your profile</h3>
                                <p style={{ color: '#64748b', marginTop: 0 }}>
                                    {profileOnly
                                        ? 'Tell us a bit about yourself while your KeyFlo access is being approved.'
                                        : 'We need a few details before you can start your free trial.'}
                                </p>
                            </div>

                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label>User Type *</label>
                                {userTypeLocked ? (
                                    <input
                                        type="text"
                                        className="wizard-input"
                                        value={formData.user_type_display || userType || ''}
                                        disabled
                                    />
                                ) : (
                                    <>
                                        <select
                                            name="user_type"
                                            className={`wizard-input ${errors.user_type ? 'error' : ''}`}
                                            value={userType || ''}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Select user type</option>
                                            {USER_TYPES.map(({ value, label }) => (
                                                <option key={value} value={value}>{label}</option>
                                            ))}
                                        </select>
                                        {errors.user_type && <span className="error-text">{errors.user_type}</span>}
                                    </>
                                )}
                            </div>

                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label>Full Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    className={`wizard-input ${errors.name ? 'error' : ''}`}
                                    value={formData.name || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.name && <span className="error-text">{errors.name}</span>}
                            </div>

                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label>Email Address</label>
                                <input type="email" className="wizard-input" value={formData.email || ''} disabled />
                            </div>

                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label>Phone Number *</label>
                                <PhoneNumberInput
                                    name="phone_number"
                                    className={`wizard-input ${errors.phone_number ? 'error' : ''}`}
                                    value={formData.phone_number || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.phone_number && <span className="error-text">{errors.phone_number}</span>}
                                <div data-sms-consent="signup">
                                    <SmsConsentNotice source="signup" />
                                </div>
                            </div>

                            {['real_estate_agent', 'managing_broker', 'mortgage_broker', 'lender', 'transaction_coordinator'].includes(userType) && (
                                <>
                                    <div className="form-group" style={{ marginBottom: '16px' }}>
                                        <label>
                                            {['mortgage_broker', 'lender'].includes(userType)
                                                ? 'NMLS Number *'
                                                : userType === 'transaction_coordinator'
                                                    ? 'License Number'
                                                    : 'License Number *'}
                                        </label>
                                        <input
                                            type="text"
                                            name="license_number"
                                            className={`wizard-input ${errors.license_number ? 'error' : ''}`}
                                            value={formData.license_number || ''}
                                            onChange={handleInputChange}
                                        />
                                        {errors.license_number && <span className="error-text">{errors.license_number}</span>}
                                    </div>
                                    {userType !== 'transaction_coordinator' && (
                                        <div className="form-group" style={{ marginBottom: '16px' }}>
                                            <label>Licensed State *</label>
                                            <input
                                                type="text"
                                                name="licensed_state"
                                                className={`wizard-input ${errors.licensed_state ? 'error' : ''}`}
                                                value={formData.licensed_state || ''}
                                                onChange={handleInputChange}
                                            />
                                            {errors.licensed_state && <span className="error-text">{errors.licensed_state}</span>}
                                        </div>
                                    )}
                                </>
                            )}

                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label>Company Name *</label>
                                <input
                                    type="text"
                                    name="company_name"
                                    className={`wizard-input ${errors.company_name ? 'error' : ''}`}
                                    value={formData.company_name || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.company_name && <span className="error-text">{errors.company_name}</span>}
                            </div>

                            {userType !== 'buyer' && userType !== 'seller' && (
                                <div className="form-group" style={{ marginBottom: '16px' }}>
                                    <label>
                                        Company Address {userType !== 'real_estate_agent' ? '*' : '(Optional)'}
                                    </label>
                                    <textarea
                                        name="company_address"
                                        className={`wizard-textarea ${errors.company_address ? 'error' : ''}`}
                                        value={formData.company_address || ''}
                                        onChange={handleInputChange}
                                        rows={3}
                                    />
                                    {errors.company_address && <span className="error-text">{errors.company_address}</span>}
                                </div>
                            )}

                            <div className="wizard-actions">
                                <button
                                    type="button"
                                    className="wizard-button primary"
                                    onClick={handleSaveProfile}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving…' : (profileOnly ? 'Save Profile' : 'Continue')}
                                </button>
                            </div>
                        </>
                    )}

                    {step === 1 && (
                        <>
                            <div className="wizard-question">
                                <h3 style={{ marginBottom: '8px' }}>Payment information</h3>
                            </div>
                            {clientSecret && stripePromise ? (
                                <Elements stripe={stripePromise} options={{ clientSecret }} key={clientSecret}>
                                    <PaymentStepForm
                                        clientSecret={clientSecret}
                                        acknowledged={acknowledged}
                                        onAcknowledgedChange={handlePaymentAckChange}
                                        legalAccepted={legalAccepted}
                                        onLegalAcceptedChange={handleLegalAcceptedChange}
                                        onSuccess={completeBillingSetup}
                                        onError={setError}
                                    />
                                </Elements>
                            ) : (
                                <>
                                    <p>Loading payment form…</p>
                                    {error ? (
                                        <div className="wizard-actions">
                                            <button
                                                type="button"
                                                className="wizard-button primary"
                                                onClick={retrySetupIntent}
                                            >
                                                Retry payment form
                                            </button>
                                        </div>
                                    ) : null}
                                </>
                            )}
                            {!profileComplete && (
                                <div className="wizard-actions">
                                    <button
                                        type="button"
                                        className="wizard-button secondary"
                                        onClick={() => setStep(0)}
                                    >
                                        Previous
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
